import { ServerMessagePayload } from '@common/api';
import { Graphics } from 'pixi.js';

import { ClientRegistry } from '../components';
import {
  ClientFilteredComponentSystem,
  FilteredClientComponentSystemUpdateEntityArgs,
} from './ClientBase';

/** Shadow fill color (black) */
const SHADOW_COLOR = 0x000000;
/** Shadow opacity - subtle enough not to distract */
const SHADOW_ALPHA = 0.2;
/** Shadow width as a proportion of collider width */
const SHADOW_WIDTH_RATIO = 0.9;
/** Shadow height as a proportion of collider height (flat oval) */
const SHADOW_HEIGHT_RATIO = 0.35;

/**
 * Renders oval shadows beneath moving entities and pickupable items.
 *
 * Shadows provide visual grounding and depth cues in the 2D world.
 * Shadow size scales proportionally to entity collider dimensions.
 *
 * Entity filtering:
 * - Requires: VisualPosition, Collider, and (Velocity OR GroundItem)
 * - Excludes: Child entities (body parts, equipped items via ChildOf)
 *
 * Cleanup:
 * - Inline check removes shadows when entity loses qualifying components
 * - Destruction callback cleans up shadows when entity is destroyed
 */
export class ShadowRenderSystem extends ClientFilteredComponentSystem {
  public componentClasses: (keyof ClientRegistry)[] = ['VisualPosition', 'Collider'];

  /**
   * Stores unregister functions for destruction callbacks.
   * Called during cleanup to prevent duplicate registrations if shadow is recreated.
   */
  private unregisterCallbacks = new Map<number, () => void>();

  updateEntity({
    world,
    entityId,
  }: FilteredClientComponentSystemUpdateEntityArgs): ServerMessagePayload[] {
    const pixiInstanceService = this.clientServices.pixiInstance;

    // Determine if entity qualifies for a shadow
    const hasVelocity = world.hasComponent(entityId, 'Velocity');
    const isGroundItem = world.hasComponent(entityId, 'GroundItem');
    const isChild = world.hasComponent(entityId, 'ChildOf');
    const isMarkedForDestruction = world.hasComponent(entityId, 'MarkedForDestruction');

    const shouldHaveShadow = (hasVelocity || isGroundItem) && !isChild && !isMarkedForDestruction;

    // Clean up shadow if entity no longer qualifies (e.g., item picked up)
    if (!shouldHaveShadow) {
      if (pixiInstanceService.hasShadow(entityId)) {
        this.cleanupShadow(entityId);
      }
      return [];
    }

    const collider = world.getComponent(entityId, 'Collider')!;
    const pos = world.getComponent(entityId, 'VisualPosition')!;
    const stage = this.clientServices.render.stage;

    if (!stage) {
      return [];
    }

    // Create shadow graphics on first encounter
    let shadow = pixiInstanceService.getShadow(entityId);
    if (!shadow) {
      shadow = new Graphics();
      stage.addChild(shadow);
      pixiInstanceService.setShadow(entityId, shadow);

      // Draw ellipse scaled to collider size
      const shadowWidth = collider.width * SHADOW_WIDTH_RATIO;
      const shadowHeight = collider.height * SHADOW_HEIGHT_RATIO;
      shadow.ellipse(0, 0, shadowWidth / 2, shadowHeight / 2);
      shadow.fill({ color: SHADOW_COLOR, alpha: SHADOW_ALPHA });

      // Register cleanup callback for when entity is destroyed
      const unregister = world.registerOnEntityDestroyed(entityId, (id) => this.cleanupShadow(id));
      this.unregisterCallbacks.set(entityId, unregister);
    }

    // Position shadow at entity's feet
    shadow.position.set(pos.x, pos.y);

    // Z-index ensures shadow renders below entity sprite (Y-sorting with offset)
    shadow.zIndex = pos.y - 1;

    return [];
  }

  /**
   * Remove and destroy shadow graphics for an entity.
   * Also removes the destruction callback to prevent duplicates if shadow is recreated.
   */
  private cleanupShadow(entityId: number): void {
    const pixiInstanceService = this.clientServices.pixiInstance;
    const stage = this.clientServices.render.stage;

    const shadow = pixiInstanceService.deleteShadow(entityId);
    if (shadow && stage) {
      stage.removeChild(shadow);
      shadow.destroy();
    }

    // Remove destruction callback to prevent duplicate registrations
    const unregister = this.unregisterCallbacks.get(entityId);
    if (unregister) {
      unregister();
      this.unregisterCallbacks.delete(entityId);
    }
  }
}
